import axiosInstance from "../../utils/axiosConfig";
import React, { useState, useEffect } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { BiErrorCircle } from "react-icons/bi";
import { FaTimesCircle } from "react-icons/fa";
import { IoMdArrowDropdown } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { MdArrowRight } from "react-icons/md";
import { TailSpin } from "react-loader-spinner";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import Profile from "./Profile";
import Kyc2 from "./Kyc2";
import Kyc3 from "./Kyc3";
import RejectionModal from "./RejectionModal";

const isNewLogShape = (d: any) => d && (d.kyc1 || d.kyc2 || d.kyc3);
const getDisplayName = (d: any) => {
  if (!d) return "";
  if (isNewLogShape(d)) {
    const k1 = d.kyc1?.data;
    if (k1?.fullName) return k1.fullName;
    return [k1?.firstName, k1?.lastName].filter(Boolean).join(" ") || "—";
  }
  return (
    [d.userId?.profile?.firstName, d.userId?.profile?.lastName]
      .filter(Boolean)
      .join(" ") || ""
  );
};
const getDisplayEmail = (d: any) => {
  if (!d) return "—";
  if (d.userId?.email?.address) return d.userId.email.address;
  if (d.user?.email?.address) return d.user.email.address;
  if (d.email) return d.email;
  return "—";
};
const isKyc2Success = (d: any) =>
  String(d?.kyc2?.status ?? "").toLowerCase() === "success";
const isKyc3NotStarted = (d: any) => {
  const s = String(d?.kyc3?.status ?? "").toLowerCase();
  return !s || s === "not-started" || s === "notstarted";
};
// Show Level 3 tab only when L2 is done and they've started L3 (pending or completed)
const showLevel3Tab = (d: any) =>
  isNewLogShape(d) && isKyc2Success(d) && !isKyc3NotStarted(d);

const Customerkyc = ({ setRefetch, customerDeet, setOpenCustomerLog }: any) => {
  const { isAuthenticated } = useAuth();
  const [openButton, setOpenButton] = useState<boolean>(false);
  const [copyMail, setCopyMail] = useState<boolean>(false);
  const [showImage, setShowImage] = useState<boolean>(false);
  const [imageUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [level, setLevel] = useState<number>(1);
  const [declineloader, setDeclineloader] = useState<boolean>(false);
  const [openReject, setOpenReject] = useState<boolean>(false);
  const [fetchedEmail, setFetchedEmail] = useState<string>("");

  useEffect(() => {
    if (!showLevel3Tab(customerDeet) && level === 3) setLevel(2);
  }, [customerDeet, level]);

  useEffect(() => {
    if (level === 1) setOpenButton(false);
  }, [level]);

  useEffect(() => {
    const uid = customerDeet?.kyc2?.data?.userId;
    if (!uid || !isAuthenticated || getDisplayEmail(customerDeet) !== "—")
      return;
    axiosInstance
      .get(`${API_URL}api/users/${uid}`)
      .then((r: any) => {
        const addr = r.data?.data?.user?.email?.address;
        if (addr) setFetchedEmail(addr);
      })
      .catch(() => {});
  }, [customerDeet, isAuthenticated]);

  const displayName = getDisplayName(customerDeet);
  const displayEmail =
    getDisplayEmail(customerDeet) !== "—"
      ? getDisplayEmail(customerDeet)
      : fetchedEmail || "—";
  const userIdForApi = customerDeet?.kyc2?.data?.userId ?? customerDeet?._id;
  const logId = customerDeet?.kyc2?.data?._id;
  const actionLevel = level === 3 ? 3 : 2;
  const rawStatus =
    level === 3
      ? customerDeet?.kyc3?.status ?? customerDeet?.kyc3?.data?.status
      : customerDeet?.kyc2?.status ?? customerDeet?.kyc2?.data?.status;
  const statusLower = String(rawStatus ?? "").toLowerCase();
  const isCompleted =
    statusLower === "success" ||
    statusLower === "approved" ||
    statusLower === "completed" ||
    statusLower === "verified";
  const isFailedOrDeclined =
    statusLower === "failed" ||
    statusLower === "declined" ||
    statusLower === "rejected";
  const showApprove = !isCompleted;
  const showDecline = !isFailedOrDeclined;

  const handleApprove = () => {
    if (loading) return;
    if (actionLevel === 3) {
      if (!userIdForApi) return;
      setLoading(true);
      axiosInstance
        .put(`${API_URL}api/users/me/kyc/4/${userIdForApi}`, {
          status: "Approved",
        })
        .then(() => {
          setLoading(false);
          setSuccess(`Level ${actionLevel} approved successfully`);
          setRefetch((prev: number) => prev + 1);
          setTimeout(() => {
            setSuccess("");
            setOpenCustomerLog(false);
          }, 3000);
        })
        .catch((e: any) => {
          setError(e.response?.data?.message ?? "Request failed");
          setLoading(false);
          setTimeout(() => setError(""), 6000);
        });
    } else {
      if (!userIdForApi || !logId) return;
      setLoading(true);
      axiosInstance
        .put(`${API_URL}api/users/me/kyc/3/${userIdForApi}/${logId}`, {
          status: "Approved",
        })
        .then(() => {
          setLoading(false);
          setSuccess(`Level ${actionLevel} approved successfully`);
          setRefetch((prev: number) => prev + 1);
          setTimeout(() => {
            setSuccess("");
            setOpenCustomerLog(false);
          }, 3000);
        })
        .catch((e: any) => {
          setError(e.response?.data?.message ?? "Request failed");
          setLoading(false);
          setTimeout(() => setError(""), 6000);
        });
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-end items-center">
        <FaTimesCircle
          onClick={() => {
            setOpenCustomerLog(false);
          }}
          className="text-[#78C2F7] text-[16px] sm:text-[18px] cursor-pointer"
        />
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full mt-4 sm:mt-6 gap-4 mb-12 sm:gap-0">
        <div className="flex justify-start items-center gap-3 sm:gap-4">
          <div className="text-[20px] sm:text-[24px] mx-auto w-[50px] sm:w-[60px] uppercase h-[50px] sm:h-[60px] bg-[#032843] rounded-full text-[#0C84DA] flex justify-center items-center font-bold font-sora">
            {displayName?.charAt(0) || "—"}
          </div>
          <div>
            <h4 className="font-sora text-[16px] sm:text-[20px] capitalize text-[#DEE1E6] font-extrabold">
              {displayName || "—"}
            </h4>
            <CopyToClipboard
              text={displayEmail}
              onCopy={() => {
                setCopyMail(true);
                setTimeout(() => setCopyMail(false), 3000);
              }}
            >
              {copyMail ? (
                <h4 className="text-[11px] sm:text-[12px] mt-1 sm:mt-2 xs:text-[14px] text-[#869AA9]">
                  Copied
                </h4>
              ) : (
                <h4 className="text-[#0C84DA] text-[12px] sm:text-[14px] font-medium cursor-pointer">
                  {displayEmail}
                </h4>
              )}
            </CopyToClipboard>
          </div>
        </div>
        {level !== 1 && (
          <div className="w-full sm:w-2/5 lgss:w-1/5 relative">
            <div className="flex w-full items-center justify-end">
              <button
                onClick={() => setOpenButton((prev) => !prev)}
                className="flex justify-between px-3 sm:px-4 outline-none gap-2 sm:gap-4 h-[40px] sm:h-[48px] rounded-xl w-full sm:w-2/3 items-center text-[#095B97] bg-[#F7F8F9] font-medium text-[14px] sm:text-[16px]"
              >
                Action
                {openButton ? (
                  <MdArrowRight className="text-[20px] sm:text-[24px]" />
                ) : (
                  <IoMdArrowDropdown className="text-[20px] sm:text-[24px]" />
                )}
              </button>
            </div>
            {openButton && (
              <div
                className={`w-full sidebar absolute bg-[#1C2227] mt-2 flex flex-col rounded-xl z-10`}
              >
                {showApprove && (
                  <button
                    onClick={() => {
                      setOpenButton(false);
                      handleApprove();
                    }}
                    className="w-full hover:bg-[#273037] text-white text-[14px] sm:text-[16px] border-b border-gray-700 font-bold py-2 sm:py-3 flex justify-center items-center"
                  >
                    {loading ? (
                      <TailSpin color="#FFFFFF" height={25} width={25} />
                    ) : (
                      `Approve Level ${level}`
                    )}
                  </button>
                )}

                {showDecline && (
                  <button
                    onClick={() => {
                      setOpenButton(false);
                      setOpenReject(true);
                    }}
                    className="text-[13px] sm:text-[15px] py-2 sm:py-3 flex justify-center items-center font-semibold text-[#DF4E4E]"
                  >
                    {declineloader ? (
                      <TailSpin color="#FFFFFF" height={25} width={25} />
                    ) : (
                      `Decline Level ${level}`
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {success && (
        <div className="bg-green-600 w-full lgss:w-1/3 xxl:w-2/5 text-white text-[14px] sm:text-[16px] rounded-xl justify-start items-center gap-2 sm:gap-4 flex h-[50px] sm:h-[60px] px-2 font-bold mt-3 sm:mt-4 mb-3 sm:mb-4">
          <AiOutlineCheckCircle className="text-2xl sm:text-3xl text-white" />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-600 w-full lgss:w-1/3 xxl:w-2/5 text-white text-[11px] sm:text-[12px] rounded-xl justify-start items-center gap-2 flex h-[40px] sm:h-[50px] px-2 font-bold mt-3 sm:mt-4">
          <BiErrorCircle className="text-lg sm:text-xl" />
          {error}
        </div>
      )}
      <div className="w-full bg-[#0D0D0D] mt-4 sm:mt-6 mb-12">
        <div className="w-full pr-4 sm:pr-8 bg-[#0D0D0D] border-b border-gray-800 h-[50px] sm:h-[67px] flex justify-between items-center overflow-x-auto">
          <div className="h-full flex justify-start items-center min-w-max">
            <button
              onClick={() => setLevel(1)}
              className={
                level === 1
                  ? "h-full border-b-2 font-bold text-[14px] sm:text-[16px] text-secondary_button border-secondary_button px-3 sm:px-4"
                  : "h-full text-[12px] sm:text-[14px] text-gray-200 px-3 sm:px-4"
              }
            >
              Level 1
            </button>
            <button
              onClick={() => setLevel(2)}
              className={
                level === 2
                  ? "h-full border-b-2 font-bold text-[14px] sm:text-[16px] text-secondary_button border-secondary_button px-3 sm:px-4"
                  : "h-full text-[12px] sm:text-[14px] text-gray-200 px-3 sm:px-4"
              }
            >
              Level 2
            </button>
            {showLevel3Tab(customerDeet) && (
              <button
                onClick={() => setLevel(3)}
                className={
                  level === 3
                    ? "h-full border-b-2 font-bold text-[14px] sm:text-[16px] text-secondary_button border-secondary_button px-3 sm:px-4"
                    : "h-full text-[12px] sm:text-[14px] text-gray-200 px-3 sm:px-4"
                }
              >
                Level 3
              </button>
            )}
          </div>
        </div>
        {level === 1 ? (
          <Profile customerDeet={customerDeet} />
        ) : level === 2 ? (
          <Kyc2 customerDeet={customerDeet} />
        ) : level === 3 ? (
          <Kyc3 customerDeet={customerDeet} />
        ) : null}
      </div>
      {showImage && (
        <div className="fixed inset-0 flex font-sora justify-center items-start pt-8 sm:pt-16 pb-8 sm:pb-16 overflow-auto bg-black bg-opacity-80">
          <div
            className={`w-11/12 mds:w-9/12 rounded-xl mx-auto p-4 sm:p-6 bg-[#1F1F1F] h-[500px] sm:h-[650px] overflow-auto lgss:w-[60%] xxl:w-[45%]`}
          >
            <div className="w-full flex justify-end items-center">
              <button
                onClick={() => {
                  setShowImage(false);
                }}
                className="w-[35px] sm:w-[40px] h-[35px] sm:h-[40px] rounded-full bg-[#007AFF] bg-opacity-10 flex justify-center items-center"
              >
                <IoClose className="text-black dark:text-white text-[12px] sm:text-[14px]" />
              </button>
            </div>
            <div className="w-full mt-4 sm:mt-6 rounded-xl flex justify-center items-center">
              {imageUrl ? (
                <img
                  src={imageUrl ?? ""}
                  className="w-full h-full object-contain rounded-xl"
                  alt=""
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
      {openReject && (
        <RejectionModal
          setOpenReject={setOpenReject}
          customerDeet={customerDeet}
          actionLevel={actionLevel}
          setError={setError}
          setDeclineloader={setDeclineloader}
          setSuccess={setSuccess}
          setRefetch={setRefetch}
          declineloader={declineloader}
          setOpenCustomerLog={setOpenCustomerLog}
        />
      )}
    </div>
  );
};

export default Customerkyc;
